import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Check, Circle, CalendarCheck } from "lucide-react";
import api from "@/lib/api";

export default function Journey() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/lessons").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-[3px] border-[var(--surface-accent)] border-t-[var(--brand)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-5 pt-8 animate-fade-rise" data-testid="journey-page">
      <p className="overline-label">The 30 day journey</p>
      <h1 className="font-serif text-3xl font-bold text-ink mt-2">Your Reset Map</h1>
      <p className="text-ink-soft text-sm mt-2">
        {data.completed_count} of 30 complete. Finish a day to unlock the next, no rush, no set dates.
      </p>

      <div className="mt-6 space-y-6">
        {data.weeks.map((w) => (
          <div key={w.week}>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-serif text-lg font-semibold text-ink">Week {w.week}</span>
              <span className="text-accent-c text-xs font-semibold tracking-wide uppercase">{w.title}</span>
            </div>
            <div className="space-y-2.5">
              {w.days.map((d) => {
                const state = d.completed ? "done" : d.current ? "current" : d.locked ? "locked" : "open";
                return (
                  <button
                    key={d.day}
                    data-testid={`journey-day-${d.day}`}
                    disabled={d.locked}
                    onClick={() => !d.locked && navigate(`/lesson/${d.day}`)}
                    className={`w-full flex items-center gap-4 rounded-2xl p-4 text-left transition-all ${
                      state === "current"
                        ? "bg-white card-shadow border-2 border-[var(--brand)]"
                        : state === "locked"
                        ? "bg-app-secondary opacity-60 cursor-not-allowed"
                        : "bg-white card-shadow active:scale-[0.99]"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        state === "done"
                          ? "bg-brand text-white"
                          : state === "current"
                          ? "bg-sage text-brand"
                          : "bg-[var(--surface-accent)] text-ink-muted"
                      }`}
                    >
                      {state === "done" ? (
                        <Check size={18} strokeWidth={3} />
                      ) : state === "locked" ? (
                        <Lock size={15} />
                      ) : (
                        <span className="font-bold text-sm">{d.day}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-ink-muted text-[11px] font-semibold">DAY {d.day}</div>
                      <div className="font-semibold text-ink text-sm">{d.title}</div>
                    </div>
                    {state === "current" && (
                      <span className="text-brand text-xs font-semibold bg-sage rounded-full px-2.5 py-1">Today</span>
                    )}
                  </button>
                );
              })}
            </div>
            {w.days.some((d) => !d.locked) && (
              <button
                data-testid={`journey-week-summary-${w.week}`}
                onClick={() => navigate(`/summary/${w.week}`)}
                className="mt-3 w-full flex items-center justify-center gap-2 text-brand text-sm font-semibold bg-sage rounded-full py-3 active:scale-[0.99] transition-transform"
              >
                <CalendarCheck size={16} /> View Week {w.week} summary
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
